import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import {
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { SITE_NAME } from '../../config/seoConfig';
import { FOOTER_ROUTES } from './footerLinks';
import { LANDING_COLORS, LANDING_FONTS } from './landingVisual';

const linkSx = {
  opacity: 0.72,
  textDecoration: 'none',
  cursor: 'pointer',
  fontFamily: LANDING_FONTS.sans,
  fontSize: '0.9rem',
  '&:hover': { opacity: 1 },
};

const buttonLinkSx = {
  ...linkSx,
  background: 'none',
  border: 'none',
  textAlign: 'left',
  padding: 0,
  font: 'inherit',
  color: 'inherit',
};

const FooterLink = ({ to, children }) => (
  <Link component={RouterLink} to={to} color="inherit" sx={linkSx}>
    {children}
  </Link>
);

const LandingFooter = ({ cpraStatus, onCpraOptOut }) => (
  <Box
    component="footer"
    sx={{
      bgcolor: LANDING_COLORS.ink,
      color: 'white',
      py: 6,
      fontFamily: LANDING_FONTS.sans,
      borderTop: '1px solid rgba(255,255,255,0.08)',
    }}
  >
    <Container maxWidth="lg">
      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ fontFamily: LANDING_FONTS.sans, fontWeight: 800, letterSpacing: '-0.03em' }}
          >
            {SITE_NAME}
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 3, opacity: 0.72, lineHeight: 1.7, fontFamily: LANDING_FONTS.sans }}
          >
            An AI-powered hiring ecosystem connecting candidates, recruiters, and interviewers —
            helping teams hire smarter and candidates get interview-ready.
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton sx={{ color: 'rgba(255,255,255,0.8)' }} aria-label="LinkedIn" size="small">
              <LinkedInIcon fontSize="small" />
            </IconButton>
            <IconButton sx={{ color: 'rgba(255,255,255,0.8)' }} aria-label="Twitter" size="small">
              <TwitterIcon fontSize="small" />
            </IconButton>
            <IconButton sx={{ color: 'rgba(255,255,255,0.8)' }} aria-label="Facebook" size="small">
              <FacebookIcon fontSize="small" />
            </IconButton>
            <IconButton sx={{ color: 'rgba(255,255,255,0.8)' }} aria-label="Email" size="small">
              <EmailIcon fontSize="small" />
            </IconButton>
          </Box>
        </Grid>

        <Grid item xs={12} md={2}>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ fontFamily: LANDING_FONTS.sans, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', opacity: 0.55, mb: 1.5 }}
          >
            Product
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FooterLink to={FOOTER_ROUTES.features}>Features</FooterLink>
            <FooterLink to="/pricing">Pricing</FooterLink>
            <FooterLink to="/jobs">Browse Jobs</FooterLink>
            <FooterLink to="/candidate">For Candidates</FooterLink>
            <FooterLink to="/become-an-interviewer">Become an Interviewer</FooterLink>
            <FooterLink to={FOOTER_ROUTES.api}>API</FooterLink>
            <FooterLink to={FOOTER_ROUTES.integrations}>Integrations</FooterLink>
          </Box>
        </Grid>

        <Grid item xs={12} md={2}>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ fontFamily: LANDING_FONTS.sans, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', opacity: 0.55, mb: 1.5 }}
          >
            Company
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FooterLink to={FOOTER_ROUTES.about}>About Us</FooterLink>
            <FooterLink to={FOOTER_ROUTES.careers}>Careers</FooterLink>
            <FooterLink to={FOOTER_ROUTES.blog}>Blog</FooterLink>
            <FooterLink to={FOOTER_ROUTES.press}>Press</FooterLink>
          </Box>
        </Grid>

        <Grid item xs={12} md={2}>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ fontFamily: LANDING_FONTS.sans, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', opacity: 0.55, mb: 1.5 }}
          >
            Support
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FooterLink to={FOOTER_ROUTES.help}>Help Center</FooterLink>
            <FooterLink to={FOOTER_ROUTES.contact}>Contact Us</FooterLink>
            <FooterLink to={FOOTER_ROUTES.status}>Status</FooterLink>
            <FooterLink to={FOOTER_ROUTES.community}>Community</FooterLink>
          </Box>
        </Grid>

        <Grid item xs={12} md={2}>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ fontFamily: LANDING_FONTS.sans, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', opacity: 0.55, mb: 1.5 }}
          >
            Legal
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FooterLink to="/privacy">Privacy Policy</FooterLink>
            <FooterLink to="/terms">Terms of Service</FooterLink>
            <FooterLink to={FOOTER_ROUTES.cookies}>Cookie Policy</FooterLink>
            <FooterLink to={FOOTER_ROUTES.gdpr}>GDPR</FooterLink>
            <Link component="button" type="button" onClick={onCpraOptOut} color="inherit" sx={buttonLinkSx}>
              Do Not Sell My Personal Information
            </Link>
            {cpraStatus?.success !== null && cpraStatus?.success !== undefined && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color={cpraStatus.success ? 'success.main' : 'error.main'}>
                  {cpraStatus.message}
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" sx={{ opacity: 0.55, fontFamily: LANDING_FONTS.sans }}>
          © {new Date().getFullYear()} {SITE_NAME}. All rights reserved.
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.55, fontFamily: LANDING_FONTS.sans }}>
          Hire smarter. Get hired faster.
        </Typography>
      </Box>
    </Container>
  </Box>
);

export default LandingFooter;
